import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import ImageCopy from 'components/ArticlePageImageCopy';
import ImagePairCopy from 'components/ArticlePageImagePairCopy';
import SecondaryPromoHero from 'components/ArticlePagePromoHero';
import CardCarousel from 'components/CardCarousel';
import EmailSignupModule from 'components/EmailSignupModule';
import { Container, Grid, Cell } from 'components/Grid';
import { PrimaryHeading } from 'components/Headings';
import { QUATERNARY_STYLES } from 'components/Headings/styles';
import VideoModal from 'components/VideoModal';
import { PAGE_SECTIONS } from 'constants/articlePage';
import { small, mediumUp, largeUp } from 'constants/mediaQueries';
import SecondaryPromoHeroModel from 'models/SecondaryPromoHero';
import Theme from 'styles/theme';

class ArticlePage extends React.PureComponent {
  constructor() {
    super();
    this.state = { modalVideoUrl: '' };
  }

  openVideoModal = url => {
    this.setState({ modalVideoUrl: url });
    this.props.openModal();
  };

  onEmailSubmit = async (email, acceptsMarketing) => {
    const { signUpForNewsletter } = this.props;

    signUpForNewsletter({
      emailAddress: email,
      origination: 'signupModule',
      acceptsMarketing,
    });
  };

  renderPageSection = data => {
    const type = get(data, '_contentType');
    const { trackEvent } = this.props;

    switch (type) {
      case PAGE_SECTIONS.IMAGE_COPY:
        return <ImageCopy {...data} />;
      case PAGE_SECTIONS.IMAGE_PAIR_COPY:
        return <ImagePairCopy {...data} />;
      case PAGE_SECTIONS.EMAIL_MODULE:
        return <EmailSignupModule onSubmit={this.onEmailSubmit} {...data} />;
      case PAGE_SECTIONS.PROMO_HERO: {
        const _data = new SecondaryPromoHeroModel(data);
        return (
          <SecondaryPromoHero
            data={_data}
            openVideoModal={this.openVideoModal}
            trackEvent={trackEvent}
          />
        );
      }
      default:
        return null;
    }
  };

  render() {
    const {
      pageSections,
      heading,
      parentPageBreadcrumb,
      relatedPages,
      isModalOpen,
      closeModal,
      trackEvent,
      modalSettings,
    } = this.props;
    const { modalVideoUrl } = this.state;

    return (
      <div className="ArticlePage">
        <VideoModal
          isOpen={isModalOpen}
          videoUrl={modalVideoUrl}
          closeModal={closeModal}
          modalSettings={modalSettings}
        />
        <Container>
          <Grid isCentered>
            {get(parentPageBreadcrumb, 'text') && (
              <Cell small={{ columns: 12 }} medium={{ columns: 11 }} largeUp={{ columns: 10 }}>
                <a
                  className="ArticlePage__breadcrumb-link typography--quaternary-heading"
                  href={get(parentPageBreadcrumb, 'url')}
                >
                  {get(parentPageBreadcrumb, 'text')}
                </a>
              </Cell>
            )}
            <Cell small={{ columns: 12 }} medium={{ columns: 11 }} largeUp={{ columns: 10 }}>
              <PrimaryHeading className="ArticlePage__heading">{heading}</PrimaryHeading>
            </Cell>
            <Cell small={{ columns: 12 }} medium={{ columns: 11 }} largeUp={{ columns: 10 }}>
              {map(pageSections, (data, i) => (
                <div key={i} className="ArticlePage__page-section">
                  {this.renderPageSection(data)}
                </div>
              ))}
            </Cell>
          </Grid>
        </Container>
        {get(relatedPages, ['carouselCards', 'length']) > 0 && (
          <CardCarousel
            data={relatedPages}
            openVideoModal={this.openVideoModal}
            trackEvent={trackEvent}
          />
        )}
        <style jsx>{QUATERNARY_STYLES}</style>
        <style jsx>
          {`
            .ArticlePage {
              background-color: ${Theme.get('colors-white')};
              padding: ${Theme.get('spacing') * 9}px 0 ${Theme.get('spacing') * 4}px;
            }

            .ArticlePage__page-section {
              padding-bottom: ${Theme.get('spacing') * 2}px;
            }

            .ArticlePage__page-section:last-of-type {
              padding-bottom: ${Theme.get('spacing') * 5}px;
            }

            @media ${mediumUp} {
              .ArticlePage {
                padding: ${Theme.get('spacing') * 14}px 0 ${Theme.get('spacing') * 7}px;
              }

              .ArticlePage__page-section {
                padding-bottom: ${Theme.get('spacing') * 5}px;
              }

              .ArticlePage__page-section:last-of-type {
                padding-bottom: ${Theme.get('spacing') * 11}px;
              }
            }

            @media ${largeUp} {
              .ArticlePage {
                padding: ${Theme.get('spacing') * 20}px 0 0;
              }

              .ArticlePage__page-section {
                padding-bottom: ${Theme.get('spacing') * 9}px;
              }

              .ArticlePage__page-section:last-of-type {
                padding-bottom: ${Theme.get('spacing') * 15}px;
              }
            }

            .ArticlePage__breadcrumb-link {
              margin-bottom: ${Theme.get('spacing') * 2}px;
              text-decoration: none;
            }

            .ArticlePage__breadcrumb-link:hover {
              text-decoration: underline;
            }
          `}
        </style>
        <style jsx global>
          {`
            .ArticlePage__heading.typography--primary-heading {
              padding-bottom: ${Theme.get('spacing') * 5}px;
            }

            @media ${mediumUp} {
              .ArticlePage__heading.typography--primary-heading {
                padding-bottom: ${Theme.get('spacing') * 7}px;
              }
            }

            @media ${largeUp} {
              .ArticlePage__heading.typography--primary-heading {
                padding-bottom: ${Theme.get('spacing') * 9}px;
              }
            }

            @media ${small} {
              .ArticlePage .Carousel {
                transform: translateX(-${Theme.get('spacing') * 2}px);
              }

              .ArticlePage .CardCarousel__heading-section {
                padding: 0;
              }
            }
          `}
        </style>
      </div>
    );
  }
}

ArticlePage.propTypes = {
  pageSections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  heading: PropTypes.string.isRequired,
  parentPageBreadcrumb: PropTypes.shape({}),
  relatedPages: PropTypes.shape({}),
  isModalOpen: PropTypes.bool,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  trackEvent: PropTypes.func,
  signUpForNewsletter: PropTypes.func,
  modalSettings: PropTypes.shape({}),
};

ArticlePage.defaultProps = {
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  trackEvent: () => {},
  signUpForNewsletter: () => {},
  modalSettings: {},
  parentPageBreadcrumb: null,
  relatedPages: null,
};

export default ArticlePage;
